import React from "react";
import { motion } from "framer-motion";
import { Upload, FileText, Download, ShieldCheck, Trash2, Search, Loader2, Clock } from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, SkeletonRows } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { toast } from "sonner";

export default function Documents({ expiring }) {
  const { user } = useAuth();
  const [docs, setDocs] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setDocs(null);
    try {
      const url = expiring ? "/documents/expiring" : "/documents";
      const { data } = await api.get(url, { params: expiring ? {} : { q, page_size: 60 } });
      setDocs(data.items);
    } catch { setDocs([]); }
  }, [q, expiring]);
  React.useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("name", file.name); fd.append("doc_type", "General");
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    if (!doc.storage_path) return toast.info("This is a demo record without an attached file.");
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename || doc.name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Unable to download this document."); }
  };

  const verify = async (doc) => { try { await api.post(`/documents/${doc.id}/verify`, { verification_status: "verified" }); toast.success("Verified"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const remove = async (doc) => { try { await api.delete(`/documents/${doc.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title={expiring ? "Expiring Documents" : "Document Center"} description={expiring ? "Documents expiring within the next 45 days." : "Upload, verify and manage compliance documents."} breadcrumb={`Documents / ${expiring ? "Expiring" : "Center"}`}>
        {!expiring && <>
          <input type="file" ref={fileRef} onChange={upload} className="hidden" data-testid="document-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-vs-royal text-white" data-testid="document-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload</Button>
        </>}
      </PageHeader>

      {!expiring && (
        <div className="relative max-w-sm mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-9" data-testid="documents-search" />
        </div>
      )}

      {docs === null ? <SkeletonRows rows={6} /> : docs.length === 0 ? (
        <EmptyState icon="FolderOpen" title={expiring ? "Nothing expiring soon" : "No documents yet"} description={expiring ? "You're all compliant." : "Upload your first document to get started."} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((doc, i) => (
            <motion.div key={doc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="vs-card vs-card-hover p-4" data-testid={`document-card-${doc.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center"><FileText className="h-5 w-5 text-vs-royal" /></div>
                <StatusBadge status={doc.verification_status} />
              </div>
              <div className="font-medium text-slate-800 text-sm mt-3 truncate">{doc.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{doc.doc_type}</div>
              {doc.expiry_date && <div className="flex items-center gap-1 text-xs text-amber-600 mt-2"><Clock className="h-3 w-3" />Expires {format(new Date(doc.expiry_date), "dd MMM yyyy")}</div>}
              <div className="flex gap-1.5 mt-4 pt-3 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => download(doc)} className="flex-1" data-testid={`doc-download-${doc.id}`}><Download className="h-3.5 w-3.5" /></Button>
                {user.role === "super_admin" && doc.verification_status !== "verified" && <Button size="sm" variant="outline" onClick={() => verify(doc)} data-testid={`doc-verify-${doc.id}`}><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /></Button>}
                {(user.role !== "super_admin") && <Button size="sm" variant="outline" onClick={() => remove(doc)} data-testid={`doc-delete-${doc.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
